/**
 * ZoneForm 서비스
 * 구역 폼 검증 및 계산 로직
 */
import type {
  ZoneFormData,
  ZoneFormErrors,
  ZoneFormValidationResult,
  ZoneFormCalculation,
  Zone,
  ZoneCreate,
  ZoneUpdate,
} from '../models/ZoneForm';

export class ZoneFormService {
  private static readonly DEFAULT_FORM_DATA: ZoneFormData = {
    name: '',
    code: '',
    description: '',
    zone_type: 'general',
    layout_type: 'grid',
    floor_level: 1,
    position_x: 0,
    position_y: 0,
    width: 400,
    height: 300,
    max_rows: 10,
    max_cols: 10,
    base_hourly_rate: 2000,
    base_daily_rate: 15000,
    hourly_surcharge: 0,
    daily_surcharge: 0,
    display_color: '#3B82F6',
    features: {},
  };

  getDefaultFormData(): ZoneFormData {
    return { ...ZoneFormService.DEFAULT_FORM_DATA };
  }

  zoneToFormData(zone: Zone): ZoneFormData {
    return {
      name: zone.name,
      code: zone.code,
      description: zone.description || '',
      zone_type: zone.zone_type,
      layout_type: zone.layout_type,
      floor_level: zone.floor_level,
      position_x: zone.position_x,
      position_y: zone.position_y,
      width: zone.width,
      height: zone.height,
      max_rows: zone.max_rows,
      max_cols: zone.max_cols,
      base_hourly_rate: zone.base_hourly_rate,
      base_daily_rate: zone.base_daily_rate,
      hourly_surcharge: zone.hourly_surcharge || 0,
      daily_surcharge: zone.daily_surcharge || 0,
      display_color: zone.display_color,
      features: zone.features || {},
    };
  }

  validateForm(formData: ZoneFormData): ZoneFormValidationResult {
    const errors: ZoneFormErrors = {};

    // 이름 검증
    if (!formData.name.trim()) {
      errors.name = '구역명을 입력해주세요';
    }

    // 코드 검증
    if (!formData.code.trim()) {
      errors.code = '구역 코드를 입력해주세요';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      errors.code = '구역 코드는 영문, 숫자, _, - 만 사용 가능합니다';
    }

    // 층수 검증
    if (formData.floor_level < 1 || formData.floor_level > 50) {
      errors.floor_level = '층수는 1~50 사이여야 합니다';
    }

    // 크기 검증
    if (formData.width < 100 || formData.width > 2000) {
      errors.width = '너비는 100~2000 사이여야 합니다';
    }

    if (formData.height < 100 || formData.height > 2000) {
      errors.height = '높이는 100~2000 사이여야 합니다';
    }

    // 행/열 검증
    if (formData.max_rows < 1 || formData.max_rows > 50) {
      errors.max_rows = '최대 행 수는 1~50 사이여야 합니다';
    }

    if (formData.max_cols < 1 || formData.max_cols > 50) {
      errors.max_cols = '최대 열 수는 1~50 사이여야 합니다';
    }

    // 요금 검증
    if (formData.base_hourly_rate < 100 || formData.base_hourly_rate > 100000) {
      errors.base_hourly_rate = '시간당 요금은 100원~100,000원 사이여야 합니다';
    }

    if (formData.base_daily_rate < 1000 || formData.base_daily_rate > 1000000) {
      errors.base_daily_rate = '일일 요금은 1,000원~1,000,000원 사이여야 합니다';
    }

    // 추가 요금 검증
    if (formData.hourly_surcharge < -50000 || formData.hourly_surcharge > 50000) {
      errors.hourly_surcharge = '시간당 추가 요금은 -50,000원~50,000원 사이여야 합니다';
    }

    if (formData.daily_surcharge < -500000 || formData.daily_surcharge > 500000) {
      errors.daily_surcharge = '일일 추가 요금은 -500,000원~500,000원 사이여야 합니다';
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  calculateRates(formData: ZoneFormData): ZoneFormCalculation {
    const suggestedDailyRate = Math.round(formData.base_hourly_rate * 8 * 0.9);
    const finalHourlyRate = formData.base_hourly_rate + formData.hourly_surcharge;
    const finalDailyRate = formData.base_daily_rate + formData.daily_surcharge;

    return {
      suggestedDailyRate,
      finalHourlyRate,
      finalDailyRate,
    };
  }

  formDataToZoneCreate(formData: ZoneFormData): ZoneCreate {
    return {
      name: formData.name,
      code: formData.code,
      description: formData.description,
      zone_type: formData.zone_type,
      layout_type: formData.layout_type,
      floor_level: formData.floor_level,
      position_x: formData.position_x,
      position_y: formData.position_y,
      width: formData.width,
      height: formData.height,
      max_rows: formData.max_rows,
      max_cols: formData.max_cols,
      base_hourly_rate: formData.base_hourly_rate,
      base_daily_rate: formData.base_daily_rate,
      hourly_surcharge: formData.hourly_surcharge,
      daily_surcharge: formData.daily_surcharge,
      display_color: formData.display_color,
      features: formData.features,
    };
  }

  formDataToZoneUpdate(formData: ZoneFormData): ZoneUpdate {
    return {
      name: formData.name,
      code: formData.code,
      description: formData.description,
      zone_type: formData.zone_type,
      layout_type: formData.layout_type,
      position_x: formData.position_x,
      position_y: formData.position_y,
      width: formData.width,
      height: formData.height,
      max_rows: formData.max_rows,
      max_cols: formData.max_cols,
      base_hourly_rate: formData.base_hourly_rate,
      base_daily_rate: formData.base_daily_rate,
      hourly_surcharge: formData.hourly_surcharge,
      daily_surcharge: formData.daily_surcharge,
      display_color: formData.display_color,
      features: formData.features,
    };
  }
}

export const zoneFormService = new ZoneFormService();
